import React, { useRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { ExcelExport, ExcelExportColumn, toDataURL } from "@progress/kendo-react-excel-export";
import dayjs from "../../../../../util/dayjs-config";

export const BADGE_COLORS = { Analyst: "brand", Associate: "brand" };

export const computeBankerDetailExportData = (filteredDeals) =>
  filteredDeals.map((row) => ({
    ...row,
    OwnersCSV: (row.Members || [])
      .filter((m) => m.IsOwner)
      .map((m) => m.User?.Name ?? "")
      .filter(Boolean)
      .join(", "),
    DealTeamCSV: (row.Members || [])
      .filter((m) => !m.IsOwner)
      .map((m) => m.User?.Name ?? "")
      .filter(Boolean)
      .join(", "),
    SeniorMembersCSV: (row.SeniorMembers || [])
      .map((m) => (typeof m === "string" ? m : m.User?.Name || m.Name) ?? "")
      .filter(Boolean)
      .join(", "),
  }));

export const handleBankerExport = async ({ summaryRef, exportRef, selectedBanker }) => {
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `${selectedBanker?.Banker ?? "Banker"} - Time Log ${timestamp}.xlsx`;
  const summaryWb = summaryRef.current?.workbookOptions();
  if (summaryWb?.sheets?.length) summaryWb.sheets[0].title = "Summary";
  const detailWb = exportRef.current?.workbookOptions();
  if (detailWb?.sheets?.length) detailWb.sheets[0].title = "Details";
  const dataUrl = await toDataURL({
    sheets: [...(summaryWb?.sheets || []), ...(detailWb?.sheets || [])],
  });
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const BankerExcelExports = ({ summaryData, detailExportData, summaryRef, exportRef }) => (
  <>
    <ExcelExport data={summaryData} ref={summaryRef}>
      <ExcelExportColumn field="Banker" title="Banker" />
      <ExcelExportColumn field="LastWeekHours" title="Total Last Wk Hrs" />
      <ExcelExportColumn field="WeekendHours" title="Total Wknd Hrs" />
      <ExcelExportColumn field="TotalHours" title="Total Actual Hrs" />
      <ExcelExportColumn field="CurrentWeekHours" title="Total Current Wk Est" />
    </ExcelExport>
    <ExcelExport data={detailExportData} ref={exportRef}>
      <ExcelExportColumn field="ProjectId" title="#" />
      <ExcelExportColumn field="ProjectName" title="Deal Name" />
      <ExcelExportColumn field="LastWeekHours" title="Last Wk Hrs" />
      <ExcelExportColumn field="WeekendHours" title="Last Wknd Hrs" />
      <ExcelExportColumn field="TotalHours" title="Total Hrs" />
      <ExcelExportColumn field="CurrentWeekHours" title="Current Wk Est" />
      <ExcelExportColumn field="OwnersCSV" title="Owners" />
      <ExcelExportColumn field="DealTeamCSV" title="Deal Team" />
      <ExcelExportColumn field="SeniorMembersCSV" title="MDs/Directors/VPs" />
      <ExcelExportColumn field="LastUpdated" title="Last Updated" />
    </ExcelExport>
  </>
);

BankerExcelExports.propTypes = {
  summaryData: PropTypes.array.isRequired,
  detailExportData: PropTypes.array.isRequired,
  summaryRef: PropTypes.object.isRequired,
  exportRef: PropTypes.object.isRequired,
};

export const BankerWidgetExcelExport = React.forwardRef(({ data }, ref) => {
  const internalRef = useRef(null);
  useImperativeHandle(ref, () => ({
    save: async () => {
      const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
      const wb = internalRef.current?.workbookOptions();
      if (!wb) return;
      const dataUrl = await toDataURL(wb);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `Banker Time Log ${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  }));
  return (
    <ExcelExport data={data} ref={internalRef}>
      <ExcelExportColumn field="Banker" title="Banker" />
      <ExcelExportColumn field="GroupName" title="Type" />
      <ExcelExportColumn field="LastWeekHours" title="Last Wk Hrs" />
      <ExcelExportColumn field="WeekendHours" title="Last Wknd Hrs" />
      <ExcelExportColumn field="TotalHours" title="Total Hrs" />
      <ExcelExportColumn field="CurrentWeekHours" title="Current Wk Est" />
      <ExcelExportColumn field="LastUpdated" title="Last Updated" />
    </ExcelExport>
  );
});

BankerWidgetExcelExport.displayName = "BankerWidgetExcelExport";

BankerWidgetExcelExport.propTypes = {
  data: PropTypes.array.isRequired,
};
